import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Alert,
  Snackbar,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Switch,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  CurrencyPound as PriceIcon,
  LocalOffer as LocalOfferIcon
} from '@mui/icons-material';
import api from '../services/api';
import { startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, format } from 'date-fns';

interface Postcode {
  id: number;
  postcode_area: string;
  created_at: string;
}

interface BankHoliday {
  id: number;
  holiday_date: string;
  holiday_name: string;
  created_at: string;
}

interface DayRestrictions {
  id?: number;
  allowed_days: {
    [key: string]: boolean;
  };
}

interface SameDayCutoff {
  enabled: boolean;
  cutoff_time: string;
}

interface SameDayZone {
  id: number;
  zone_name: string;
  cutoff_time: string;
  postcodes: string[];
  days: string[];
  is_active: boolean;
}

interface DayRestrictionZone {
  id: number;
  zone_name: string;
  allowed_days: { [key: string]: boolean };
  postcodes: string[];
}

interface WooProduct {
  product_id: number;
  name: string;
  base_price: string;
  slug?: string;
}

// Only these (one-off) products may be given away free — a BOGOF free bin must be a
// single one-off collection, never a multi-week/recurring product.
const ONE_OFF_SLUGS = [
  'bin-empty',
  'bag-collection',
  'large-bin-empty-one-off',
  'bed-collection-any-size',
  'mattress-collection-any-size'
];

interface ZoneProductPrice {
  product_id: number;
  product_name: string;
  price: number;
}

interface PostcodePricingZone {
  id: number;
  zone_name: string;
  postcodes: string[];
  product_prices: ZoneProductPrice[];
  is_active: boolean;
}

interface BogofSettings {
  enabled: boolean;
  deal_dates: string[];
  trigger_product_ids: number[];
  free_product_id: number | null;
  banner_text: string;
  top_banner_enabled: boolean;
  sub_banner_enabled: boolean;
  sub_banner_text: string;
}

const CollectionDays: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  // Postcodes
  const [postcodes, setPostcodes] = useState<Postcode[]>([]);
  const [openPostcodeDialog, setOpenPostcodeDialog] = useState(false);
  const [newPostcode, setNewPostcode] = useState('');
  const [postcodeFilter, setPostcodeFilter] = useState('');

  // Bank Holidays
  const [holidays, setHolidays] = useState<BankHoliday[]>([]);
  const [openHolidayDialog, setOpenHolidayDialog] = useState(false);
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayName, setNewHolidayName] = useState('');
  const [holidayFilter, setHolidayFilter] = useState('');

  // Day Restrictions
  const [dayRestrictions, setDayRestrictions] = useState<DayRestrictions>({
    allowed_days: {
      "1": true,
      "2": true,
      "3": true,
      "4": true,
      "5": true,
      "6": false,
      "7": false
    }
  });

  // Same Day Cutoff
  const [sameDayCutoff, setSameDayCutoff] = useState<SameDayCutoff>({
    enabled: false,
    cutoff_time: '12:00'
  });
  const [savingSameDay, setSavingSameDay] = useState(false);

  // Same Day Zones
  const [sameDayZones, setSameDayZones] = useState<SameDayZone[]>([]);
  const [openZoneDialog, setOpenZoneDialog] = useState(false);
  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneCutoff, setNewZoneCutoff] = useState('12:00');
  const [newZonePostcodes, setNewZonePostcodes] = useState<string[]>([]);
  const [newZoneDays, setNewZoneDays] = useState<string[]>([]);
  const [savingZone, setSavingZone] = useState(false);
  const [editingZoneId, setEditingZoneId] = useState<number | null>(null);

  // Day Restriction Zones
  const [dayRestrictionZones, setDayRestrictionZones] = useState<DayRestrictionZone[]>([]);
  const [openDayZoneDialog, setOpenDayZoneDialog] = useState(false);
  const [newDayZoneName, setNewDayZoneName] = useState('');
  const [newDayZoneAllowedDays, setNewDayZoneAllowedDays] = useState<{ [key: string]: boolean }>({
    "1": true, "2": true, "3": true, "4": true, "5": true, "6": false, "7": false
  });
  const [newDayZonePostcodes, setNewDayZonePostcodes] = useState<string[]>([]);
  const [savingDayZone, setSavingDayZone] = useState(false);
  const [editingDayZoneId, setEditingDayZoneId] = useState<number | null>(null);

  // Postcode Pricing
  const [pricingZones, setPricingZones] = useState<PostcodePricingZone[]>([]);
  const [wooProducts, setWooProducts] = useState<WooProduct[]>([]);
  const [openPricingDialog, setOpenPricingDialog] = useState(false);
  const [editingPricingZoneId, setEditingPricingZoneId] = useState<number | null>(null);
  const [pricingZoneName, setPricingZoneName] = useState('');
  const [pricingPostcodes, setPricingPostcodes] = useState<string[]>([]);
  const [pricingPostcodeInput, setPricingPostcodeInput] = useState('');
  const [pricingProductPrices, setPricingProductPrices] = useState<Record<number, string>>({});
  const [pricingProductEnabled, setPricingProductEnabled] = useState<Record<number, boolean>>({});
  const [savingPricingZone, setSavingPricingZone] = useState(false);

  // BOGOF / Deals
  const [bogof, setBogof] = useState<BogofSettings>({
    enabled: false,
    deal_dates: [],
    trigger_product_ids: [],
    free_product_id: null,
    banner_text: '',
    top_banner_enabled: false,
    sub_banner_enabled: false,
    sub_banner_text: ''
  });
  const [dealCalMonth, setDealCalMonth] = useState<Date>(new Date());
  const [savingBogof, setSavingBogof] = useState(false);

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const dayNames: { [key: string]: string } = {
    "1": "Monday",
    "2": "Tuesday",
    "3": "Wednesday",
    "4": "Thursday",
    "5": "Friday",
    "6": "Saturday",
    "7": "Sunday"
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [postcodesRes, holidaysRes, daysRes, sameDayRes, zonesRes, dayZonesRes, pricingRes, productsRes, bogofRes] = await Promise.all([
        api.get('/collection-days/postcodes'),
        api.get('/collection-days/bank-holidays'),
        api.get('/collection-days/day-restrictions'),
        api.get('/collection-days/same-day-cutoff'),
        api.get('/collection-days/same-day-zones'),
        api.get('/collection-days/day-restriction-zones'),
        api.get('/collection-days/postcode-pricing/zones'),
        api.get('/collection-days/postcode-pricing/products'),
        api.get('/collection-days/deals/bogof')
      ]);

      setPostcodes(postcodesRes.data);
      setHolidays(holidaysRes.data);
      if (daysRes.data.allowed_days) {
        setDayRestrictions(daysRes.data);
      }
      if (sameDayRes.data) {
        setSameDayCutoff(sameDayRes.data);
      }
      setSameDayZones(zonesRes.data);
      setDayRestrictionZones(dayZonesRes.data);
      setPricingZones(pricingRes.data);
      setWooProducts(productsRes.data);
      if (bogofRes.data) {
        setBogof({
          enabled: !!bogofRes.data.enabled,
          deal_dates: bogofRes.data.deal_dates || [],
          trigger_product_ids: bogofRes.data.trigger_product_ids || [],
          free_product_id: bogofRes.data.free_product_id ?? null,
          banner_text: bogofRes.data.banner_text || '',
          top_banner_enabled: !!bogofRes.data.top_banner_enabled,
          sub_banner_enabled: !!bogofRes.data.sub_banner_enabled,
          sub_banner_text: bogofRes.data.sub_banner_text || ''
        });
      }
    } catch (error) {
      showSnackbar('Failed to load data', 'error');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // ====================
  // POSTCODE FUNCTIONS
  // ====================

  const handleAddPostcode = async () => {
    if (!newPostcode) {
      showSnackbar('Please enter a postcode', 'error');
      return;
    }

    try {
      await api.post('/collection-days/postcodes', { postcode_area: newPostcode.toUpperCase() });
      showSnackbar('Postcode added successfully', 'success');
      setOpenPostcodeDialog(false);
      setNewPostcode('');
      fetchData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to add postcode', 'error');
    }
  };

  const handleDeletePostcode = async (id: number) => {
    if (!window.confirm('Remove this postcode from the collection area?')) return;

    try {
      await api.delete(`/collection-days/postcodes/${id}`);
      showSnackbar('Postcode removed successfully', 'success');
      fetchData();
    } catch (error) {
      showSnackbar('Failed to remove postcode', 'error');
    }
  };

  // ====================
  // BANK HOLIDAY FUNCTIONS
  // ====================

  const handleAddHoliday = async () => {
    if (!newHolidayDate || !newHolidayName) {
      showSnackbar('Please enter date and name', 'error');
      return;
    }

    try {
      await api.post('/collection-days/bank-holidays', {
        holiday_date: newHolidayDate,
        holiday_name: newHolidayName
      });
      showSnackbar('Bank holiday added successfully', 'success');
      setOpenHolidayDialog(false);
      setNewHolidayDate('');
      setNewHolidayName('');
      fetchData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to add bank holiday', 'error');
    }
  };

  const handleDeleteHoliday = async (id: number) => {
    if (!window.confirm('Remove this bank holiday?')) return;

    try {
      await api.delete(`/collection-days/bank-holidays/${id}`);
      showSnackbar('Bank holiday removed successfully', 'success');
      fetchData();
    } catch (error) {
      showSnackbar('Failed to remove bank holiday', 'error');
    }
  };

  // ====================
  // DAY RESTRICTIONS FUNCTIONS
  // ====================

  const handleToggleDay = (day: string) => {
    setDayRestrictions(prev => ({
      ...prev,
      allowed_days: {
        ...prev.allowed_days,
        [day]: !prev.allowed_days[day]
      }
    }));
  };

  const handleSaveDayRestrictions = async () => {
    try {
      await api.post('/collection-days/day-restrictions', {
        allowed_days: dayRestrictions.allowed_days
      });
      showSnackbar('Day restrictions updated successfully', 'success');
      fetchData();
    } catch (error) {
      showSnackbar('Failed to update day restrictions', 'error');
    }
  };

  // ====================
  // SAME DAY CUTOFF FUNCTIONS
  // ====================

  const handleSaveSameDayCutoff = async () => {
    setSavingSameDay(true);
    try {
      await api.post('/collection-days/same-day-cutoff', sameDayCutoff);
      showSnackbar('Same-day collection settings saved successfully', 'success');
      fetchData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to save same-day settings', 'error');
    } finally {
      setSavingSameDay(false);
    }
  };

  // ====================
  // DEALS / BOGOF FUNCTIONS
  // ====================

  const toggleDealDate = (d: Date) => {
    const iso = format(d, 'yyyy-MM-dd');
    setBogof(prev => ({
      ...prev,
      deal_dates: prev.deal_dates.includes(iso)
        ? prev.deal_dates.filter(x => x !== iso)
        : [...prev.deal_dates, iso].sort()
    }));
  };

  const handleRemoveBogofDate = (date: string) => {
    setBogof(prev => ({ ...prev, deal_dates: prev.deal_dates.filter(d => d !== date) }));
  };

  const handleToggleBogofProduct = (productId: number) => {
    setBogof(prev => ({
      ...prev,
      trigger_product_ids: prev.trigger_product_ids.includes(productId)
        ? prev.trigger_product_ids.filter(id => id !== productId)
        : [...prev.trigger_product_ids, productId]
    }));
  };

  const handleToggleAllBogofProducts = () => {
    setBogof(prev => ({
      ...prev,
      trigger_product_ids: prev.trigger_product_ids.length === wooProducts.length
        ? []
        : wooProducts.map(p => p.product_id)
    }));
  };

  const handleSaveBogof = async () => {
    if (bogof.enabled && bogof.trigger_product_ids.length === 0) {
      showSnackbar('Pick at least one qualifying product before enabling the deal', 'error');
      return;
    }
    if (bogof.enabled && !bogof.free_product_id) {
      showSnackbar('Choose the free product before enabling the deal', 'error');
      return;
    }
    setSavingBogof(true);
    try {
      await api.post('/collection-days/deals/bogof', bogof);
      showSnackbar('BOGOF deal settings saved successfully', 'success');
      fetchData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to save BOGOF settings', 'error');
    } finally {
      setSavingBogof(false);
    }
  };

  // ====================
  // SAME DAY ZONE FUNCTIONS
  // ====================

  const getPostcodesInOtherZones = (): Record<string, string> => {
    const map: Record<string, string> = {};
    sameDayZones.forEach(zone => {
      if (zone.id === editingZoneId) return; // skip the zone being edited
      zone.postcodes.forEach(pc => {
        map[pc] = zone.zone_name;
      });
    });
    return map;
  };

  const postcodeGroups: Record<string, string[]> = (() => {
    const groups: Record<string, string[]> = {};
    postcodes.forEach(pc => {
      const prefix = pc.postcode_area.replace(/\d+$/, '');
      if (!groups[prefix]) groups[prefix] = [];
      groups[prefix].push(pc.postcode_area);
    });
    // Sort postcodes within each group numerically
    Object.keys(groups).forEach(prefix => {
      groups[prefix].sort((a, b) => {
        const numA = parseInt(a.replace(/^[A-Z]+/, ''), 10);
        const numB = parseInt(b.replace(/^[A-Z]+/, ''), 10);
        return numA - numB;
      });
    });
    return groups;
  })();

  const handleToggleZonePostcode = (pc: string) => {
    setNewZonePostcodes(prev =>
      prev.includes(pc) ? prev.filter(p => p !== pc) : [...prev, pc]
    );
  };

  const handleAddZone = async () => {
    if (!newZoneName.trim()) {
      showSnackbar('Please enter a zone name', 'error');
      return;
    }
    if (newZonePostcodes.length === 0 && newZoneDays.length === 0) {
      showSnackbar('Please select at least one postcode or day', 'error');
      return;
    }

    setSavingZone(true);
    try {
      const payload = {
        zone_name: newZoneName.trim(),
        cutoff_time: newZoneCutoff,
        postcodes: newZonePostcodes,
        days: newZoneDays
      };

      if (editingZoneId) {
        await api.put(`/collection-days/same-day-zones/${editingZoneId}`, payload);
        showSnackbar('Zone updated successfully', 'success');
      } else {
        await api.post('/collection-days/same-day-zones', payload);
        showSnackbar('Zone created successfully', 'success');
      }
      setOpenZoneDialog(false);
      setNewZoneName('');
      setNewZoneCutoff('12:00');
      setNewZonePostcodes([]);
      setNewZoneDays([]);
      setEditingZoneId(null);
      fetchData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to save zone', 'error');
    } finally {
      setSavingZone(false);
    }
  };

  const handleEditZone = (zone: SameDayZone) => {
    const defaultDays = Object.entries(dayRestrictions.allowed_days).filter(([, v]) => v).map(([k]) => k);
    setEditingZoneId(zone.id);
    setNewZoneName(zone.zone_name);
    setNewZoneCutoff(zone.cutoff_time);
    setNewZonePostcodes([...zone.postcodes]);
    setNewZoneDays(zone.days && zone.days.length > 0 ? [...zone.days] : defaultDays);
    setOpenZoneDialog(true);
  };

  const handleDeleteZone = async (id: number) => {
    if (!window.confirm('Delete this cutoff zone?')) return;

    try {
      await api.delete(`/collection-days/same-day-zones/${id}`);
      showSnackbar('Zone deleted successfully', 'success');
      fetchData();
    } catch (error) {
      showSnackbar('Failed to delete zone', 'error');
    }
  };

  const handleToggleZone = async (id: number) => {
    try {
      await api.patch(`/collection-days/same-day-zones/${id}/toggle`);
      fetchData();
    } catch (error) {
      showSnackbar('Failed to toggle zone', 'error');
    }
  };

  // ====================
  // DAY RESTRICTION ZONE FUNCTIONS
  // ====================

  const getPostcodesInOtherDayZones = (): Record<string, string> => {
    const map: Record<string, string> = {};
    dayRestrictionZones.forEach(zone => {
      if (zone.id === editingDayZoneId) return;
      zone.postcodes.forEach(pc => {
        map[pc] = zone.zone_name;
      });
    });
    return map;
  };

  const handleToggleDayZonePostcode = (pc: string) => {
    setNewDayZonePostcodes(prev =>
      prev.includes(pc) ? prev.filter(p => p !== pc) : [...prev, pc]
    );
  };

  const handleToggleDayZoneDay = (day: string) => {
    setNewDayZoneAllowedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const handleAddDayZone = async () => {
    if (!newDayZoneName.trim()) {
      showSnackbar('Please enter a zone name', 'error');
      return;
    }
    if (newDayZonePostcodes.length === 0) {
      showSnackbar('Please select at least one postcode', 'error');
      return;
    }
    const hasAnyDay = Object.values(newDayZoneAllowedDays).some(v => v);
    if (!hasAnyDay) {
      showSnackbar('Please select at least one allowed day', 'error');
      return;
    }

    setSavingDayZone(true);
    try {
      const payload = {
        zone_name: newDayZoneName.trim(),
        allowed_days: newDayZoneAllowedDays,
        postcodes: newDayZonePostcodes
      };

      if (editingDayZoneId) {
        await api.put(`/collection-days/day-restriction-zones/${editingDayZoneId}`, payload);
        showSnackbar('Day restriction zone updated successfully', 'success');
      } else {
        await api.post('/collection-days/day-restriction-zones', payload);
        showSnackbar('Day restriction zone created successfully', 'success');
      }
      setOpenDayZoneDialog(false);
      setNewDayZoneName('');
      setNewDayZoneAllowedDays({ "1": true, "2": true, "3": true, "4": true, "5": true, "6": false, "7": false });
      setNewDayZonePostcodes([]);
      setEditingDayZoneId(null);
      fetchData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to save day restriction zone', 'error');
    } finally {
      setSavingDayZone(false);
    }
  };

  const handleEditDayZone = (zone: DayRestrictionZone) => {
    setEditingDayZoneId(zone.id);
    setNewDayZoneName(zone.zone_name);
    setNewDayZoneAllowedDays({ ...zone.allowed_days });
    setNewDayZonePostcodes([...zone.postcodes]);
    setOpenDayZoneDialog(true);
  };

  const handleDeleteDayZone = async (id: number) => {
    if (!window.confirm('Delete this day restriction zone?')) return;

    try {
      await api.delete(`/collection-days/day-restriction-zones/${id}`);
      showSnackbar('Day restriction zone deleted successfully', 'success');
      fetchData();
    } catch (error) {
      showSnackbar('Failed to delete day restriction zone', 'error');
    }
  };

  // ====================
  // POSTCODE PRICING FUNCTIONS
  // ====================

  const resetPricingZoneDialog = () => {
    setPricingZoneName('');
    setPricingPostcodes([]);
    setPricingPostcodeInput('');
    const defaultPrices: Record<number, string> = {};
    const defaultEnabled: Record<number, boolean> = {};
    wooProducts.forEach(p => {
      if (p.base_price) defaultPrices[p.product_id] = parseFloat(p.base_price).toFixed(2);
      defaultEnabled[p.product_id] = false;
    });
    setPricingProductPrices(defaultPrices);
    setPricingProductEnabled(defaultEnabled);
    setEditingPricingZoneId(null);
  };

  const handleAddPricingPostcode = () => {
    const val = pricingPostcodeInput.trim().toUpperCase();
    if (!val) return;
    if (!pricingPostcodes.includes(val)) {
      setPricingPostcodes(prev => [...prev, val]);
    }
    setPricingPostcodeInput('');
  };

  const handleSavePricingZone = async () => {
    if (!pricingZoneName.trim()) { showSnackbar('Please enter a zone name', 'error'); return; }
    if (pricingPostcodes.length === 0) { showSnackbar('Please add at least one postcode', 'error'); return; }

    const productPricesArray: ZoneProductPrice[] = Object.entries(pricingProductPrices)
      .filter(([productId, price]) => price !== '' && parseFloat(price) > 0)
      .map(([productId, price]) => {
        const product = wooProducts.find(p => p.product_id === Number(productId));
        return {
          product_id: Number(productId),
          product_name: product?.name || '',
          price: parseFloat(price)
        };
      });

    if (productPricesArray.length === 0) {
      showSnackbar('Please set a price for at least one service', 'error');
      return;
    }

    setSavingPricingZone(true);
    try {
      const payload = {
        zone_name: pricingZoneName.trim(),
        postcodes: pricingPostcodes,
        product_prices: productPricesArray
      };
      if (editingPricingZoneId) {
        await api.put(`/collection-days/postcode-pricing/zones/${editingPricingZoneId}`, payload);
        showSnackbar('Pricing zone updated successfully', 'success');
      } else {
        await api.post('/collection-days/postcode-pricing/zones', payload);
        showSnackbar('Pricing zone created successfully', 'success');
      }
      setOpenPricingDialog(false);
      resetPricingZoneDialog();
      fetchData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to save pricing zone', 'error');
    } finally {
      setSavingPricingZone(false);
    }
  };

  const handleEditPricingZone = (zone: PostcodePricingZone) => {
    setEditingPricingZoneId(zone.id);
    setPricingZoneName(zone.zone_name);
    setPricingPostcodes([...zone.postcodes]);
    setPricingPostcodeInput('');
    const priceMap: Record<number, string> = {};
    const enabledMap: Record<number, boolean> = {};
    // seed all products with base price, disabled by default
    wooProducts.forEach(p => {
      if (p.base_price) priceMap[p.product_id] = parseFloat(p.base_price).toFixed(2);
      enabledMap[p.product_id] = false;
    });
    // enable and override price for products that have a saved zone price
    zone.product_prices.forEach(pp => {
      priceMap[pp.product_id] = String(pp.price);
      enabledMap[pp.product_id] = true;
    });
    setPricingProductPrices(priceMap);
    setPricingProductEnabled(enabledMap);
    setOpenPricingDialog(true);
  };

  const handleDeletePricingZone = async (id: number) => {
    if (!window.confirm('Delete this pricing zone?')) return;
    try {
      await api.delete(`/collection-days/postcode-pricing/zones/${id}`);
      showSnackbar('Pricing zone deleted successfully', 'success');
      fetchData();
    } catch (error) {
      showSnackbar('Failed to delete pricing zone', 'error');
    }
  };

  const handleTogglePricingZone = async (id: number) => {
    try {
      await api.patch(`/collection-days/postcode-pricing/zones/${id}/toggle`);
      fetchData();
    } catch (error) {
      showSnackbar('Failed to toggle pricing zone', 'error');
    }
  };

  // ====================
  // UPDATE CHECKOUT CALENDAR
  // ====================

  // NO LONGER NEEDED - Changes are instant via REST API
  // const handleUpdateCheckoutCalendar = async () => {
  //   ...removed...
  // };

  // ====================
  // RENDER
  // ====================

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Checkout Settings
      </Typography>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tab label="Postcode Areas" icon={<LocationIcon />} iconPosition="start" />
        <Tab label="Bank Holidays" icon={<EventIcon />} iconPosition="start" />
        <Tab label="Day Restrictions" icon={<CalendarIcon />} iconPosition="start" />
        <Tab label="Same Day Collection" icon={<ScheduleIcon />} iconPosition="start" />
        <Tab label="Out of Area Pricing" icon={<PriceIcon />} iconPosition="start" />
        <Tab label="Deals" icon={<LocalOfferIcon />} iconPosition="start" />
      </Tabs>

      {/* TAB 1: POSTCODES */}
      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Allowed Postcode Areas ({postcodes.length} total)</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenPostcodeDialog(true)}
                sx={{ bgcolor: '#23a244' }}
              >
                Add Postcode
              </Button>
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Search postcodes... (e.g., SK, M1, BL)"
              value={postcodeFilter}
              onChange={(e) => setPostcodeFilter(e.target.value)}
              sx={{ mb: 3 }}
            />

            {postcodes.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                No postcodes configured
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {postcodes
                  .filter(pc =>
                    postcodeFilter === '' ||
                    pc.postcode_area.toLowerCase().includes(postcodeFilter.toLowerCase())
                  )
                  .map((pc) => (
                    <Chip
                      key={pc.id}
                      label={pc.postcode_area}
                      onDelete={() => handleDeletePostcode(pc.id)}
                      color="primary"
                      sx={{ fontSize: '0.875rem', height: '36px' }}
                    />
                  ))}
              </Box>
            )}

            {postcodeFilter && postcodes.filter(pc =>
              pc.postcode_area.toLowerCase().includes(postcodeFilter.toLowerCase())
            ).length === 0 && (
              <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                No postcodes match "{postcodeFilter}"
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* TAB 2: BANK HOLIDAYS */}
      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Bank Holidays ({holidays.filter(h => new Date(h.holiday_date) >= new Date(new Date().setHours(0, 0, 0, 0))).length} upcoming)
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenHolidayDialog(true)}
                sx={{ bgcolor: '#23a244' }}
              >
                Add Bank Holiday
              </Button>
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Search holidays... (e.g., Christmas, 2025)"
              value={holidayFilter}
              onChange={(e) => setHolidayFilter(e.target.value)}
              sx={{ mb: 3 }}
            />

            {holidays.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                No bank holidays configured
              </Box>
            ) : (
              (() => {
                // Filter out past dates and apply search filter
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const filteredHolidays = holidays.filter(h => {
                  const holidayDate = new Date(h.holiday_date);
                  const isUpcoming = holidayDate >= today;
                  const matchesSearch = holidayFilter === '' ||
                    h.holiday_name.toLowerCase().includes(holidayFilter.toLowerCase()) ||
                    h.holiday_date.includes(holidayFilter);

                  return isUpcoming && matchesSearch;
                });

                // Group by year
                const groupedByYear = filteredHolidays.reduce((acc: any, holiday) => {
                  const year = new Date(holiday.holiday_date).getFullYear();
                  if (!acc[year]) acc[year] = [];
                  acc[year].push(holiday);
                  return acc;
                }, {});

                const years = Object.keys(groupedByYear).sort();

                if (filteredHolidays.length === 0) {
                  return (
                    <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                      No holidays match "{holidayFilter}"
                    </Box>
                  );
                }

                return years.map(year => (
                  <Box key={year} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                      {year}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {groupedByYear[year].map((holiday: BankHoliday) => (
                        <Chip
                          key={holiday.id}
                          label={`${new Date(holiday.holiday_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${holiday.holiday_name}`}
                          onDelete={() => handleDeleteHoliday(holiday.id)}
                          color="secondary"
                          sx={{ fontSize: '0.875rem', height: 'auto', py: 1, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                        />
                      ))}
                    </Box>
                  </Box>
                ));
              })()
            )}
          </CardContent>
        </Card>
      )}

      {/* TAB 3: DAY RESTRICTIONS */}
      {tabValue === 2 && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Default Allowed Days
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                These are the default days customers can book collections. {dayRestrictionZones.length > 0 ? 'Postcodes not in any zone below will use these defaults.' : 'Applies to ALL postcodes.'}
              </Typography>

              <FormGroup row>
                {Object.keys(dayNames).map((day) => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={dayRestrictions.allowed_days[day]}
                        onChange={() => handleToggleDay(day)}
                      />
                    }
                    label={dayNames[day as keyof typeof dayNames]}
                  />
                ))}
              </FormGroup>

              <Button
                variant="contained"
                onClick={handleSaveDayRestrictions}
                sx={{ mt: 2, bgcolor: '#23a244' }}
              >
                Save Default Days
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Day Restriction Zones</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => { setEditingDayZoneId(null); setNewDayZoneName(''); setNewDayZoneAllowedDays({ "1": true, "2": true, "3": true, "4": true, "5": true, "6": false, "7": false }); setNewDayZonePostcodes([]); setOpenDayZoneDialog(true); }}
                  sx={{ bgcolor: '#23a244' }}
                >
                  Add Zone
                </Button>
              </Box>

              {dayRestrictionZones.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No zones created — the default allowed days above apply to <strong>ALL postcodes</strong>. Create zones to set different allowed days for specific postcode areas.
                </Alert>
              ) : (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Postcodes assigned to a zone use that zone's allowed days. Postcodes NOT in any zone fall back to the default allowed days above.
                  </Alert>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Zone Name</strong></TableCell>
                          <TableCell><strong>Allowed Days</strong></TableCell>
                          <TableCell><strong>Postcodes</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dayRestrictionZones.map((zone) => (
                          <TableRow key={zone.id}>
                            <TableCell>{zone.zone_name}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {Object.keys(dayNames).filter(d => zone.allowed_days[d]).map(d => (
                                  <Chip key={d} label={dayNames[d].slice(0, 3)} size="small" color="success" />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {zone.postcodes.map(pc => (
                                  <Chip key={pc} label={pc} size="small" color="primary" variant="outlined" />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleEditDayZone(zone)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteDayZone(zone.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* TAB 4: SAME DAY COLLECTION */}
      {tabValue === 3 && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Same Day Collection
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Allow customers to select today as their collection date, if they order before the cutoff time.
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={sameDayCutoff.enabled}
                      onChange={(e) => setSameDayCutoff(prev => ({ ...prev, enabled: e.target.checked }))}
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="body1" fontWeight="bold">
                      {sameDayCutoff.enabled ? 'Same-day collection is ON' : 'Same-day collection is OFF'}
                    </Typography>
                  }
                />
              </Box>

              {sameDayCutoff.enabled && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Default Cutoff Time (used when no zones are configured)
                  </Typography>
                  <TextField
                    type="time"
                    value={sameDayCutoff.cutoff_time}
                    onChange={(e) => setSameDayCutoff(prev => ({ ...prev, cutoff_time: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    sx={{ width: 200 }}
                  />
                </Box>
              )}

              <Alert severity={sameDayCutoff.enabled ? 'success' : 'info'} sx={{ mb: 3 }}>
                {sameDayCutoff.enabled
                  ? `Customers can select today for collection if they order before ${sameDayCutoff.cutoff_time}. After the cutoff, the earliest available date will be the next working day.`
                  : 'Same-day collection is disabled. The earliest available collection date is always the next working day.'}
              </Alert>

              <Button
                variant="contained"
                onClick={handleSaveSameDayCutoff}
                disabled={savingSameDay}
                sx={{ bgcolor: '#23a244' }}
              >
                {savingSameDay ? 'Saving...' : 'Save Settings'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Cutoff Zones</Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => { const defaultDays = Object.entries(dayRestrictions.allowed_days).filter(([, v]) => v).map(([k]) => k); setEditingZoneId(null); setNewZoneName(''); setNewZoneCutoff('12:00'); setNewZonePostcodes([]); setNewZoneDays(defaultDays); setOpenZoneDialog(true); }}
                    sx={{ bgcolor: '#23a244' }}
                  >
                    Add Zone
                  </Button>
                </Box>

                {sameDayZones.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {sameDayCutoff.enabled
                      ? <>No zones created — same-day collection applies to <strong>ALL postcodes</strong> using the global cutoff time above. Create zones to set different cutoff times per area.</>
                      : <>Same-day collection is currently <strong>turned off</strong>. You can still create and manage zones here — they will take effect as soon as you enable same-day collection above. With no zones, same-day will apply to <strong>ALL postcodes</strong> using the global cutoff time. Create zones to set different cutoff times per area.</>}
                  </Alert>
                ) : (
                  <>
                    <Alert severity={sameDayCutoff.enabled ? 'warning' : 'info'} sx={{ mb: 2 }}>
                      {sameDayCutoff.enabled
                        ? 'Postcodes not assigned to any zone won\'t have same-day collection. Postcodes in a zone get same-day if ordered before the zone\'s cutoff time.'
                        : <>Same-day collection is currently <strong>turned off</strong>. Your {sameDayZones.length} zone{sameDayZones.length > 1 ? 's are' : ' is'} ready but inactive — they will take effect as soon as you enable same-day collection above. Once enabled, postcodes in a zone get same-day if ordered before that zone's cutoff time.</>}
                    </Alert>

                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Zone Name</strong></TableCell>
                            <TableCell><strong>Cutoff Time</strong></TableCell>
                            <TableCell><strong>Days</strong></TableCell>
                            <TableCell><strong>Postcodes</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sameDayZones.map((zone) => (
                            <TableRow key={zone.id}>
                              <TableCell>{zone.zone_name}</TableCell>
                              <TableCell>{zone.cutoff_time}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {(() => {
                                    const dayLabels: Record<string, string> = { '1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun' };
                                    const zoneDays = zone.days || [];
                                    if (zoneDays.length > 0) {
                                      return zoneDays.map(d => (
                                        <Chip key={d} label={dayLabels[d] || d} size="small" color="secondary" variant="outlined" />
                                      ));
                                    }
                                    // Fall back to global day restrictions
                                    const defaultDays = Object.entries(dayRestrictions.allowed_days)
                                      .filter(([, enabled]) => enabled)
                                      .map(([d]) => d);
                                    return defaultDays.map(d => (
                                      <Chip key={d} label={dayLabels[d] || d} size="small" color="default" variant="outlined" sx={{ opacity: 0.7 }} />
                                    ));
                                  })()}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {zone.postcodes.map(pc => (
                                    <Chip key={pc} label={pc} size="small" color="primary" variant="outlined" />
                                  ))}
                                  {zone.postcodes.length === 0 && <Typography variant="body2" color="text.secondary">—</Typography>}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={zone.is_active ? 'Active' : 'Inactive'}
                                  size="small"
                                  color={zone.is_active ? 'success' : 'default'}
                                />
                                <Switch
                                  checked={zone.is_active}
                                  onChange={() => handleToggleZone(zone.id)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleEditZone(zone)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleDeleteZone(zone.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </CardContent>
            </Card>
        </>
      )}

      {/* DIALOG: ADD POSTCODE */}
      <Dialog open={openPostcodeDialog} onClose={() => setOpenPostcodeDialog(false)}>
        <DialogTitle>Add Postcode Area</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Postcode Area (e.g., SK14, M1)"
            fullWidth
            value={newPostcode}
            onChange={(e) => setNewPostcode(e.target.value.toUpperCase())}
            placeholder="SK14"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPostcodeDialog(false)}>Cancel</Button>
          <Button onClick={handleAddPostcode} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: ADD BANK HOLIDAY */}
      <Dialog open={openHolidayDialog} onClose={() => setOpenHolidayDialog(false)}>
        <DialogTitle>Add Bank Holiday</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Date"
            type="date"
            fullWidth
            value={newHolidayDate}
            onChange={(e) => setNewHolidayDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Holiday Name"
            fullWidth
            value={newHolidayName}
            onChange={(e) => setNewHolidayName(e.target.value)}
            placeholder="Christmas Day"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHolidayDialog(false)}>Cancel</Button>
          <Button onClick={handleAddHoliday} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: ADD ZONE */}
      <Dialog open={openZoneDialog} onClose={() => setOpenZoneDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingZoneId ? 'Edit Cutoff Zone' : 'Add Cutoff Zone'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Zone Name"
            fullWidth
            value={newZoneName}
            onChange={(e) => setNewZoneName(e.target.value)}
            placeholder="e.g., South Manchester"
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Cutoff Time"
            type="time"
            value={newZoneCutoff}
            onChange={(e) => setNewZoneCutoff(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
            sx={{ mb: 3, width: 200 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Apply to Days
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
            {[
              { num: '1', label: 'Mon' },
              { num: '2', label: 'Tue' },
              { num: '3', label: 'Wed' },
              { num: '4', label: 'Thu' },
              { num: '5', label: 'Fri' },
              { num: '6', label: 'Sat' },
              { num: '7', label: 'Sun' },
            ].filter(({ num }) => dayRestrictions.allowed_days[num]).map(({ num, label }) => {
              const isSelected = newZoneDays.includes(num);
              return (
                <Chip
                  key={num}
                  label={label}
                  size="small"
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  onClick={() => setNewZoneDays(prev => prev.includes(num) ? prev.filter(d => d !== num) : [...prev, num])}
                  sx={{ cursor: 'pointer' }}
                />
              );
            })}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Select Postcodes ({newZonePostcodes.length} selected)
          </Typography>

          {Object.keys(postcodeGroups).sort().map(prefix => {
            const groupPostcodes = postcodeGroups[prefix];
            const otherZones = getPostcodesInOtherZones();
            const allSelected = groupPostcodes.every(pc => newZonePostcodes.includes(pc));
            const handleSelectAll = () => {
              if (allSelected) {
                setNewZonePostcodes(prev => prev.filter(pc => !groupPostcodes.includes(pc)));
              } else {
                setNewZonePostcodes(prev => Array.from(new Set([...prev, ...groupPostcodes])));
              }
            };
            return (
              <Box key={prefix} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {prefix} postcodes ({groupPostcodes.length})
                  </Typography>
                  <Chip
                    label={allSelected ? 'Deselect All' : 'Select All'}
                    size="small"
                    color={allSelected ? 'primary' : 'default'}
                    variant="outlined"
                    onClick={handleSelectAll}
                    sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 20 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {groupPostcodes.map(pc => {
                    const inOtherZone = otherZones[pc] || null;
                    const isSelected = newZonePostcodes.includes(pc);
                    return (
                      <Chip
                        key={pc}
                        label={inOtherZone ? `${pc} (${inOtherZone})` : pc}
                        size="small"
                        color={isSelected ? 'primary' : (inOtherZone ? 'warning' : 'default')}
                        variant={isSelected ? 'filled' : 'outlined'}
                        onClick={() => handleToggleZonePostcode(pc)}
                        sx={{ cursor: 'pointer' }}
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenZoneDialog(false); setNewZoneName(''); setNewZoneCutoff('12:00'); setNewZonePostcodes([]); setNewZoneDays([]); setEditingZoneId(null); }}>Cancel</Button>
          <Button onClick={handleAddZone} variant="contained" disabled={savingZone}>
            {savingZone ? 'Saving...' : (editingZoneId ? 'Save Changes' : 'Create Zone')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: ADD/EDIT DAY RESTRICTION ZONE */}
      <Dialog open={openDayZoneDialog} onClose={() => setOpenDayZoneDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingDayZoneId ? 'Edit Day Restriction Zone' : 'Add Day Restriction Zone'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Zone Name"
            fullWidth
            value={newDayZoneName}
            onChange={(e) => setNewDayZoneName(e.target.value)}
            placeholder="e.g., South Manchester"
            sx={{ mb: 2, mt: 1 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Allowed Days for this Zone
          </Typography>
          <FormGroup row sx={{ mb: 3 }}>
            {Object.keys(dayNames).map((day) => (
              <FormControlLabel
                key={day}
                control={
                  <Checkbox
                    checked={newDayZoneAllowedDays[day] || false}
                    onChange={() => handleToggleDayZoneDay(day)}
                    color="success"
                  />
                }
                label={dayNames[day as keyof typeof dayNames]}
              />
            ))}
          </FormGroup>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Select Postcodes ({newDayZonePostcodes.length} selected)
          </Typography>

          {Object.keys(postcodeGroups).sort().map(prefix => {
            const groupPostcodes = postcodeGroups[prefix];
            const otherZones = getPostcodesInOtherDayZones();
            return (
              <Box key={prefix} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mb: 0.5 }}>
                  {prefix} postcodes ({groupPostcodes.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {groupPostcodes.map(pc => {
                    const inOtherZone = otherZones[pc] || null;
                    const isSelected = newDayZonePostcodes.includes(pc);
                    return (
                      <Chip
                        key={pc}
                        label={inOtherZone ? `${pc} (${inOtherZone})` : pc}
                        size="small"
                        color={isSelected ? 'primary' : (inOtherZone ? 'warning' : 'default')}
                        variant={isSelected ? 'filled' : 'outlined'}
                        onClick={() => handleToggleDayZonePostcode(pc)}
                        sx={{ cursor: 'pointer' }}
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDayZoneDialog(false); setNewDayZoneName(''); setNewDayZoneAllowedDays({ "1": true, "2": true, "3": true, "4": true, "5": true, "6": false, "7": false }); setNewDayZonePostcodes([]); setEditingDayZoneId(null); }}>Cancel</Button>
          <Button onClick={handleAddDayZone} variant="contained" disabled={savingDayZone}>
            {savingDayZone ? 'Saving...' : (editingDayZoneId ? 'Save Changes' : 'Create Zone')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TAB 5: POSTCODE PRICING */}
      {tabValue === 4 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6">Postcode Pricing Zones ({pricingZones.length} total)</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Create outside-area zones with their own postcodes and per-service prices. Services with no zone match use their standard WooCommerce price.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { resetPricingZoneDialog(); setOpenPricingDialog(true); }}
                sx={{ bgcolor: '#23a244' }}
              >
                Add Zone
              </Button>
            </Box>

{pricingZones.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                No pricing zones configured. Add a zone to charge different prices for specific outside-area postcodes.
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Zone Name</strong></TableCell>
                      <TableCell><strong>Postcodes</strong></TableCell>
                      <TableCell><strong>Service Prices</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pricingZones.map((zone) => (
                      <TableRow key={zone.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{zone.zone_name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 250 }}>
                            {zone.postcodes.map(pc => (
                              <Chip key={pc} label={pc} size="small" color="primary" variant="outlined" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {zone.product_prices.map(pp => (
                              <Box key={pp.product_id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption">{pp.product_name}:</Typography>
                                <Chip label={`£${Number(pp.price).toFixed(2)}`} size="small" color="warning" />
                              </Box>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={zone.is_active ? 'Active' : 'Inactive'}
                              color={zone.is_active ? 'success' : 'default'}
                              size="small"
                            />
                            <Switch
                              size="small"
                              checked={zone.is_active}
                              onChange={() => handleTogglePricingZone(zone.id)}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleEditPricingZone(zone)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeletePricingZone(zone.id)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* TAB 6: DEALS (BOGOF) */}
      {tabValue === 5 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Special Deal – BOGOF (Buy One Get One Free)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Offer a free bin on chosen "deal days". When live, qualifying customers can add one free bin at checkout.
            </Typography>

            {/* On/off switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={bogof.enabled}
                  onChange={(e) => setBogof(prev => ({ ...prev, enabled: e.target.checked }))}
                  color="success"
                />
              }
              label={
                <Typography variant="body1" fontWeight="bold">
                  {bogof.enabled ? 'BOGOF deal is ON' : 'BOGOF deal is OFF'}
                </Typography>
              }
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
              Turn on for a manual deal day, or leave off and use the deal dates below to auto-run on specific days.
            </Typography>

            {/* Qualifying products */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Which products the offer applies to</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              The deal shows when a customer has one of these products in their basket.
            </Typography>
            <FormGroup sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={wooProducts.length > 0 && bogof.trigger_product_ids.length === wooProducts.length}
                    indeterminate={bogof.trigger_product_ids.length > 0 && bogof.trigger_product_ids.length < wooProducts.length}
                    onChange={handleToggleAllBogofProducts}
                  />
                }
                label={<Typography variant="body2" fontWeight="bold">Select all</Typography>}
              />
              {wooProducts.map(product => (
                <FormControlLabel
                  key={product.product_id}
                  control={
                    <Checkbox
                      checked={bogof.trigger_product_ids.includes(product.product_id)}
                      onChange={() => handleToggleBogofProduct(product.product_id)}
                    />
                  }
                  label={product.name}
                />
              ))}
            </FormGroup>

            {/* Free product */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Which product is given free</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Only one-off products can be given free (the free bin is a single collection, never a multi-week one).
            </Typography>
            <TextField
              select
              SelectProps={{ native: true }}
              size="small"
              value={bogof.free_product_id ?? ''}
              onChange={(e) => setBogof(prev => ({ ...prev, free_product_id: e.target.value ? Number(e.target.value) : null }))}
              sx={{ minWidth: 320, mb: 3 }}
            >
              <option value="">— Select a one-off product —</option>
              {wooProducts.filter(p => p.slug && ONE_OFF_SLUGS.includes(p.slug)).map(product => (
                <option key={product.product_id} value={product.product_id}>{product.name}</option>
              ))}
            </TextField>

            {/* Deal dates */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Deal days (optional)</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              On these dates the offer runs automatically, even if the switch above is off. Click days to select as many as you like.
            </Typography>
            <Box sx={{ maxWidth: 300, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <IconButton size="small" onClick={() => setDealCalMonth(m => subMonths(m, 1))}>‹</IconButton>
                <Typography variant="subtitle2">{format(dealCalMonth, 'MMMM yyyy')}</Typography>
                <IconButton size="small" onClick={() => setDealCalMonth(m => addMonths(m, 1))}>›</IconButton>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d, i) => (
                  <Typography key={i} variant="caption" align="center" color="text.secondary">{d}</Typography>
                ))}
                {Array.from({ length: (startOfMonth(dealCalMonth).getDay() + 6) % 7 }).map((_, i) => (
                  <Box key={'blank' + i} />
                ))}
                {eachDayOfInterval({ start: startOfMonth(dealCalMonth), end: endOfMonth(dealCalMonth) }).map(d => {
                  const iso = format(d, 'yyyy-MM-dd');
                  const selected = bogof.deal_dates.includes(iso);
                  return (
                    <Button
                      key={iso}
                      size="small"
                      onClick={() => toggleDealDate(d)}
                      variant={selected ? 'contained' : 'text'}
                      sx={{ minWidth: 0, p: 0.5, ...(selected ? { bgcolor: '#23a244', '&:hover': { bgcolor: '#1c8237' } } : {}) }}
                    >
                      {format(d, 'd')}
                    </Button>
                  );
                })}
              </Box>
            </Box>
            {bogof.deal_dates.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                {bogof.deal_dates.map(date => (
                  <Chip
                    key={date}
                    label={new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    onDelete={() => handleRemoveBogofDate(date)}
                    color="secondary"
                    size="small"
                  />
                ))}
              </Box>
            )}

            {/* Banners */}
            <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 'bold' }}>Promo banners</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Switch each on or off on its own. The top banner sits above the header site-wide; the homepage box is placed wherever you like using a shortcode.
            </Typography>

            {/* Top banner (above header) */}
            <FormControlLabel
              control={
                <Switch
                  checked={bogof.top_banner_enabled}
                  onChange={(e) => setBogof(prev => ({ ...prev, top_banner_enabled: e.target.checked }))}
                  color="success"
                />
              }
              label={<Typography variant="body2" fontWeight="bold">Top banner (above the header) — {bogof.top_banner_enabled ? 'ON' : 'OFF'}</Typography>}
            />
            <TextField
              fullWidth
              size="small"
              value={bogof.banner_text}
              onChange={(e) => setBogof(prev => ({ ...prev, banner_text: e.target.value }))}
              placeholder="e.g. 🎉 BOGOF Today – Buy One Bin, Get One Free!"
              inputProps={{ maxLength: 255 }}
              sx={{ mb: 3, mt: 1 }}
            />

            {/* Homepage deal box (shortcode) */}
            <FormControlLabel
              control={
                <Switch
                  checked={bogof.sub_banner_enabled}
                  onChange={(e) => setBogof(prev => ({ ...prev, sub_banner_enabled: e.target.checked }))}
                  color="success"
                />
              }
              label={<Typography variant="body2" fontWeight="bold">Homepage deal box (orange) — {bogof.sub_banner_enabled ? 'ON' : 'OFF'}</Typography>}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, mt: 0.5 }}>
              Add this shortcode where you want the orange box (e.g. an Elementor Shortcode widget on the homepage): <code style={{ background: '#eee', padding: '1px 5px', borderRadius: 4 }}>[bnb_bogof_teaser]</code>
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={bogof.sub_banner_text}
              onChange={(e) => setBogof(prev => ({ ...prev, sub_banner_text: e.target.value }))}
              placeholder="e.g. Keep checking for our random BOGOF deal days through June & July!"
              inputProps={{ maxLength: 255 }}
              sx={{ mb: 3, mt: 1 }}
            />

            <Box>
              <Button variant="contained" onClick={handleSaveBogof} disabled={savingBogof} sx={{ bgcolor: '#23a244' }}>
                {savingBogof ? 'Saving...' : 'Save Deal Settings'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* POSTCODE PRICING ZONE DIALOG */}
      <Dialog open={openPricingDialog} onClose={() => { setOpenPricingDialog(false); resetPricingZoneDialog(); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPricingZoneId ? 'Edit Pricing Zone' : 'Add Pricing Zone'}</DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto', maxHeight: '70vh' }}>
          {/* Zone Name */}
          <TextField
            label="Zone Name"
            placeholder="e.g. Outer Area, Greater Manchester"
            value={pricingZoneName}
            onChange={(e) => setPricingZoneName(e.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 3 }}
          />

          {/* Postcode Entry */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Postcodes in this zone ({pricingPostcodes.length} added)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              label="Add Postcode"
              placeholder="e.g. SK14"
              value={pricingPostcodeInput}
              onChange={(e) => setPricingPostcodeInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPricingPostcode(); } }}
              size="small"
              sx={{ flex: 1 }}
            />
            <Button variant="outlined" onClick={handleAddPricingPostcode} size="small">Add</Button>
          </Box>
          {pricingPostcodes.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
              {pricingPostcodes.map(pc => (
                <Chip
                  key={pc}
                  label={pc}
                  size="small"
                  color="primary"
                  onDelete={() => setPricingPostcodes(prev => prev.filter(p => p !== pc))}
                />
              ))}
            </Box>
          )}

          {/* Per-product prices */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Service prices for this zone
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Set a price for the services you want to charge differently in this zone. Leave blank for standard price.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {wooProducts.map(product => (
                <Box key={product.product_id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {product.name}
                    {product.base_price && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (standard: £{parseFloat(product.base_price).toFixed(2)})
                      </Typography>
                    )}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Zone price"
                    value={pricingProductPrices[product.product_id] || ''}
                    onChange={(e) => setPricingProductPrices(prev => ({
                      ...prev,
                      [product.product_id]: e.target.value
                    }))}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                    sx={{ width: 160 }}
                  />
                </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenPricingDialog(false); resetPricingZoneDialog(); }}>Cancel</Button>
          <Button onClick={handleSavePricingZone} variant="contained" disabled={savingPricingZone}>
            {savingPricingZone ? 'Saving...' : (editingPricingZoneId ? 'Save Changes' : 'Create Zone')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CollectionDays;
